import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import HowItWorksGraphic from '../components/HowItWorksGraphic';
import { images } from '../data/mockData';
import { getResponsiveImageUrl } from '../lib/imageOptimization';

const HowItWorksPage = () => {
  const handleScheduleSession = () => {
    window.open('https://twnhub.online/services', '_blank', 'noopener noreferrer');
  };

  const steps = [
    {
      number: "1",
      title: "Explore and Choose Your Practitioner",
      description: "Click on the \"Schedule Your Session Today\" Button and select one of our certified spiritual life coaches, herbalists, psychic readers, astrologers, healers, manifestation coaches, and other holistic practitioners."
    },
    {
      number: "2", 
      title: "Book Your Session",
      description: "Once you've found the practitioner who resonates with you, simply click to book your session with them. Our easy-to-use online booking system allows you to select a convenient time for your session. You'll receive a confirmation email with all the details, including a Zoom link for your appointment."
    },
    {
      number: "3",
      title: "Experience Your Transformation!",
      description: "On the day of your session, ensure you are in a quiet and comfortable space where you can focus without distractions. Connect with your practitioner via Zoom at the scheduled time for personalized guidance and support. After your session, embrace the insights and guidance you received and get ready for a powerful transformation. Consider journaling your thoughts and any action steps suggested by your practitioner to integrate the experience fully."
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Header Section */}
      <section className="pt-16 pb-8 bg-gradient-to-br from-purple-100 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-purple-800 mb-6 font-title">
            How it Works
          </h1>
          <p className="text-lg text-purple-700 leading-relaxed mb-4 font-body">
            We're excited to guide you on your journey towards personal growth, healing, and transformation. 
            Please note that all appointments are done virtually via Zoom video call. Here's how you can get 
            started with our platform in three simple steps:
          </p>
        </div>
      </section>

      {/* Three-step booking graphic */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-12">
            <a 
              href="https://thewisdomnetwork.as.me/"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full max-w-full rounded-lg shadow-lg transition-shadow duration-300 hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-purple-700"
            >
              <HowItWorksGraphic />
            </a>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <Card 
                key={index}
                className="bg-white border border-purple-200 hover:border-purple-400 transition-all duration-300 hover:transform hover:scale-102"
              >
                <CardContent className="p-8 text-center">
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-purple-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-2xl font-title">{step.number}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-purple-800 mb-4 font-subtitle">
                    {step.title}
                  </h3>
                  
                  <p className="text-purple-700 leading-relaxed font-body">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Repeated Steps Section (as shown in original) */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {steps.map((step, index) => (
              <div key={`repeat-${index}`} className="text-center">
                <h2 className="text-3xl font-bold text-purple-800 mb-4 font-title">
                  {step.number}. {step.title}
                </h2>
                <p className="text-lg text-purple-700 leading-relaxed font-body max-w-3xl mx-auto">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-purple-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-title">
              START YOUR JOURNEY TODAY!
            </h2>
            <div className="text-2xl text-yellow-200 mb-8 font-subtitle">
              START YOUR JOURNEY TODAY!<span className="mx-4">START YOUR JOURNEY TODAY!</span>START YOUR JOURNEY TODAY!
            </div>
          </div>

          <Button 
            onClick={handleScheduleSession}
            className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-4 px-8 text-xl rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl font-body mb-12"
          >
            SCHEDULE YOUR SESSION
          </Button>
        </div>
      </section>

      {/* Bottom Branding Section */}
      <section className="py-16 bg-purple-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <img
              src={getResponsiveImageUrl(images.spiritual, { width: 600 })}
              alt="The Wisdom Network"
              className="w-32 h-20 mx-auto rounded-lg object-cover shadow-lg"
              loading="lazy"
              decoding="async"
            />
          </div>

          <h2 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-4 font-title">
            THE WISDOM NETWORK:
          </h2>
          
          <p className="text-xl text-yellow-200 font-semibold font-subtitle">
            VERIFIED WISDOM, POWERFUL TRANSFORMATIONS
          </p>
        </div>
      </section>

    </div>
  );
};

export default HowItWorksPage;